/**
 * Whether this device still owes the first-run Welcome (#1095), and the two facts it rests on.
 *
 * The Welcome shows on a device's first launch, signed out, until Get started is tapped
 * once. That is a per-device flag in `localStorage`. A storage that refuses it just shows the
 * Welcome again on the next launch: the store keeps the answer for this one, so the person
 * is not sent back to it from Sign in.
 */

/** Named like the theme's own key (`meetfolks-theme`). */
export const WELCOME_KEY = "meetfolks-welcome-done";

/** Whether Get started has been tapped on this device. Unreadable storage reads as no. */
export function readWelcomeDone(storage: Pick<Storage, "getItem"> | null = safeStorage()): boolean {
  try {
    return storage?.getItem(WELCOME_KEY) === "1";
  } catch {
    return false;
  }
}

/** Remember that this device is past the Welcome. A blocked storage forgets it at the next launch. */
export function writeWelcomeDone(storage: Pick<Storage, "setItem"> | null = safeStorage()): void {
  try {
    storage?.setItem(WELCOME_KEY, "1");
  } catch {
    // Shown again next time, which is all a blocked storage costs.
  }
}

/**
 * Whether this launch was a plain open of the app: `/` with nothing after it.
 *
 * Anything else is an entry link and goes where it goes today: a magic link (`/#signin=`,
 * which boot clears from the URL before any screen draws, so this is read at launch), an
 * old notes link (`/?session=`), the deploy gate's `/?probe=1`, or any other path.
 */
export function isPlainLaunch(location: Pick<Location, "pathname" | "search" | "hash">): boolean {
  return location.pathname === "/" && location.search === "" && location.hash === "";
}

/** `localStorage`, or null where even reading the property throws (some private modes). */
function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
